import React, { useState, useEffect, useMemo } from 'react';
import { Users, Plus, Edit, Trash2, Shield, Building, Mail, Calendar, X } from 'lucide-react';
import { useDataTableFilters, DataTableFilter, createColumnConfigHelper, applyFilters } from '@/components/data-table-filter';
import { AppShell } from '@/components/layout/AppShell';
import { Card, Button, Badge, Modal, Input, Spinner, Column } from '@/components/ui';
import { ResponsiveTable as DataTable } from '@/components/ui/responsive-table';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useDateFormat } from '@/hooks/useDateFormat';
import { adminResetMfa } from '@/services/mfaService';
import { adminResetUserPassword, getAdminUsersActivity } from '@/services/adminUserService';
import { useNavigate } from 'react-router-dom';
import { toDate } from '@/lib/date-utils';
import { formatRelativeTime } from '@/lib/utils';

interface Organization {
  id: string;
  name: string;
}

interface User {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string | null;
  systemRole: 'super_admin' | 'support' | null;
  organizations: Array<{
    organizationId: string;
    role: 'owner' | 'admin' | 'member' | 'viewer';
  }>;
  createdAt: Date;
  emailVerified?: boolean;
  /** Epoch ms, from Firebase Auth. Undefined until loaded; null = never signed in. */
  lastSignInAt?: number | null;
  lastActiveAt?: number | null;
}

// Filter column configuration for bazza/ui DataTableFilter
const filterColumnHelper = createColumnConfigHelper<User>();

// Static filter configurations that don't depend on component state
const getStaticFilterConfigs = () => [
  filterColumnHelper
    .text()
    .id('displayName')
    .accessor((row) => row.displayName)
    .displayName('Name')
    .icon(Users)
    .build(),
  filterColumnHelper
    .text()
    .id('email')
    .accessor((row) => row.email)
    .displayName('Email')
    .icon(Mail)
    .build(),
  filterColumnHelper
    .option()
    .id('systemRole')
    .accessor((row) => row.systemRole || 'none')
    .displayName('System Role')
    .icon(Shield)
    .options([
      { label: 'Super Admin', value: 'super_admin' },
      { label: 'Support', value: 'support' },
      { label: 'Regular User', value: 'none' },
    ])
    .build(),
  filterColumnHelper
    .option()
    .id('createdAt')
    .accessor((row) => {
      if (!row.createdAt) return 'all';
      const now = new Date();
      const created = row.createdAt instanceof Date ? row.createdAt : new Date(row.createdAt);
      const diffMs = now.getTime() - created.getTime();
      const diffDays = diffMs / (1000 * 60 * 60 * 24);
      if (diffDays <= 1) return '24h';
      if (diffDays <= 7) return '7d';
      if (diffDays <= 30) return '30d';
      if (diffDays <= 90) return '90d';
      return 'older';
    })
    .displayName('Created')
    .icon(Calendar)
    .options([
      { label: 'Last 24 hours', value: '24h' },
      { label: 'Last 7 days', value: '7d' },
      { label: 'Last 30 days', value: '30d' },
      { label: 'Last 90 days', value: '90d' },
      { label: 'Older', value: 'older' },
    ])
    .build(),
];

type OrgRole = 'owner' | 'admin' | 'member' | 'viewer';
type OrgAssignment = { orgId: string; role: OrgRole };

export function UsersPage() {
  const { isSuperAdmin } = useAuth();
  const { success, error: showError } = useToast();
  const fmt = useDateFormat();
  const navigate = useNavigate();

  const [users, setUsers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);

  // Create user modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Edit/Delete modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Edit form state
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editSystemRole, setEditSystemRole] = useState<'super_admin' | 'support' | null>(null);
  const [editOrgAssignments, setEditOrgAssignments] = useState<OrgAssignment[]>([]);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  // Result of an admin password reset — shown once, since the plaintext is not
  // recoverable afterward. Holds the target email so the admin can copy both.
  const [pwResetResult, setPwResetResult] = useState<{ email: string | null; password: string } | null>(null);
  const [pwResetLoading, setPwResetLoading] = useState(false);
  const [pwCopied, setPwCopied] = useState(false);

  // Delete state
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [createEmail, setCreateEmail] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createDisplayName, setCreateDisplayName] = useState('');
  const [createSystemRole, setCreateSystemRole] = useState<'super_admin' | 'support' | null>(null);
  const [createOrgAssignments, setCreateOrgAssignments] = useState<OrgAssignment[]>([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Build dynamic filter config with organization options from state
  const filterColumnsConfig = useMemo(() => {
    return [
      ...getStaticFilterConfigs(),
      filterColumnHelper
        .option()
        .id('organizations')
        .accessor((row) => {
          // Return comma-separated org IDs for filtering
          // This allows multi-select to match any of the user's organizations
          return row.organizations.map(o => o.organizationId).join(',');
        })
        .displayName('Organizations')
        .icon(Building)
        .options(organizations.map(org => ({ label: org.name, value: org.id })))
        .build(),
    ];
  }, [organizations]);

  // bazza/ui DataTableFilter hook
  const { columns: filterColumns, filters, actions, strategy } = useDataTableFilters({
    strategy: 'client',
    data: users,
    columnsConfig: filterColumnsConfig,
  });

  // Apply filters to users data
  const filteredUsers = useMemo(() =>
    applyFilters(users, filters, filterColumnsConfig),
    [users, filters, filterColumnsConfig]
  );

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/');
      return;
    }
    loadData();
  }, [isSuperAdmin]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load organizations from Firestore
      const { getFirestore, collection, getDocs } = await import('firebase/firestore');
      const db = getFirestore();
      
      const orgsSnapshot = await getDocs(collection(db, 'organizations'));
      const orgsData: Organization[] = [];
      orgsSnapshot.forEach(doc => {
        orgsData.push({
          id: doc.id,
          name: doc.data().name,
        });
      });
      setOrganizations(orgsData);
      
      // Load users from Firestore
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData: User[] = [];
      
      usersSnapshot.forEach(doc => {
        const data = doc.data();
        usersData.push({
          uid: doc.id,
          email: data.email || '',
          displayName: data.displayName || '',
          photoURL: data.photoURL || null,
          systemRole: data.systemRole || null,
          organizations: data.organizations || [],
          createdAt: toDate(data.createdAt) || new Date(),
          emailVerified: data.metadata?.emailVerified || false,
        });
      });
      
      setUsers(usersData);

      // Best-effort: overlay sign-in times from Auth. Never block the list on it.
      getAdminUsersActivity()
        .then(({ activity }) => setUsers(prev => prev.map(u => ({
          ...u,
          lastSignInAt: activity[u.uid]?.lastSignInAt ?? null,
          lastActiveAt: activity[u.uid]?.lastActiveAt ?? null,
        }))))
        .catch((e) => console.warn('Failed to load user activity:', e));
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!createEmail || !createPassword || !createDisplayName) {
      setCreateError('Email, password, and display name are required');
      return;
    }

    if (createPassword.length < 6) {
      setCreateError('Password must be at least 6 characters');
      return;
    }

    if (!createSystemRole && createOrgAssignments.length === 0) {
      setCreateError('Must assign user to at least one organization OR set a system role');
      return;
    }

    if (createOrgAssignments.some(a => !a.orgId)) {
      setCreateError('All organization rows must have an organization selected');
      return;
    }

    try {
      setCreating(true);
      setCreateError('');

      // Create user in Firebase Authentication using a secondary app instance
      // so the admin's current session is not replaced by the newly created user's session.
      const { initializeApp, getApps, deleteApp } = await import('firebase/app');
      const { getAuth, createUserWithEmailAndPassword, updateProfile } = await import('firebase/auth');
      const { app: primaryApp } = await import('@/config/firebase');
      const auth = getAuth(primaryApp);

      const secondaryAppName = 'secondary-user-creation';
      const existingSecondary = getApps().find(a => a.name === secondaryAppName);
      if (existingSecondary) await deleteApp(existingSecondary);

      const secondaryApp = initializeApp(primaryApp.options, secondaryAppName);
      const secondaryAuth = getAuth(secondaryApp);

      let userCredential;
      try {
        userCredential = await createUserWithEmailAndPassword(
          secondaryAuth,
          createEmail,
          createPassword
        );

        await updateProfile(userCredential.user, {
          displayName: createDisplayName,
        });
      } finally {
        await deleteApp(secondaryApp);
      }

      // Create user profile in Firestore
      const { getFirestore, doc, setDoc, serverTimestamp } = await import('firebase/firestore');
      const db = getFirestore();
      
      const userProfile: any = {
        email: createEmail,
        displayName: createDisplayName,
        photoURL: null,
        systemRole: createSystemRole,
        organizations: [],
        currentOrganizationId: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        preferences: {
          theme: 'dark',
          language: 'en',
          notifications: true,
        },
        metadata: {
          emailVerified: userCredential.user.emailVerified,
          provider: 'password',
          lastOrganizationId: null,
        },
      };

      // Assign organizations
      if (!createSystemRole && createOrgAssignments.length > 0) {
        const now = new Date();
        userProfile.organizations = createOrgAssignments.map(a => ({
          organizationId: a.orgId,
          role: a.role,
          joinedAt: now,
        }));
        userProfile.currentOrganizationId = createOrgAssignments[0].orgId;
        userProfile.metadata.lastOrganizationId = createOrgAssignments[0].orgId;
      }

      await setDoc(doc(db, 'users', userCredential.user.uid), userProfile);

      // Write membership subcollections
      if (!createSystemRole && createOrgAssignments.length > 0) {
        const { setDoc: fsSetDoc, doc: fsDoc } = await import('firebase/firestore');
        const now = new Date();
        for (const assignment of createOrgAssignments) {
          await fsSetDoc(fsDoc(db, 'organizations', assignment.orgId, 'members', userCredential.user.uid), {
            userId: userCredential.user.uid,
            email: createEmail,
            displayName: createDisplayName,
            role: assignment.role,
            joinedAt: now,
            invitedBy: auth.currentUser?.uid || 'system',
          });
          await fsSetDoc(fsDoc(db, 'users', userCredential.user.uid, 'organizations', assignment.orgId), {
            organizationId: assignment.orgId,
            role: assignment.role,
            joinedAt: now,
          });
        }
      }

      // Success!
      success(`User created successfully! Email: ${createEmail} | Password: ${createPassword}`);
      
      // Reset form
      setIsCreateModalOpen(false);
      setCreateEmail('');
      setCreatePassword('');
      setCreateDisplayName('');
      setCreateSystemRole(null);
      setCreateOrgAssignments([]);
      
      // Reload users
      loadData();
      
    } catch (error: any) {
      console.error('Failed to create user:', error);
      setCreateError(error.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    setEditDisplayName(user.displayName);
    setEditSystemRole(user.systemRole);
    setEditOrgAssignments(user.organizations.map(o => ({ orgId: o.organizationId, role: o.role })));
    setEditError('');
    setIsEditModalOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser || !editDisplayName) {
      setEditError('Display name is required');
      return;
    }

    if (editOrgAssignments.some(a => !a.orgId)) {
      setEditError('All organization rows must have an organization selected');
      return;
    }

    setEditLoading(true);
    setEditError('');

    try {
      const { getFirestore, doc, updateDoc, setDoc, deleteDoc, collection, getDocs, query, where } = await import('firebase/firestore');
      const db = getFirestore();
      const now = new Date();

      const originalOrgIds = new Set(selectedUser.organizations.map(o => o.organizationId));
      const newOrgIds = new Set(editOrgAssignments.map(a => a.orgId));

      // Update user profile with the full new org list
      const updatedOrganizations = editOrgAssignments.map(a => ({
        organizationId: a.orgId,
        role: a.role,
        joinedAt: selectedUser.organizations.find(o => o.organizationId === a.orgId)
          ? (selectedUser.organizations.find(o => o.organizationId === a.orgId) as any).joinedAt ?? now
          : now,
      }));

      await updateDoc(doc(db, 'users', selectedUser.uid), {
        displayName: editDisplayName,
        systemRole: editSystemRole,
        organizations: updatedOrganizations,
        updatedAt: now,
      });

      // Remove memberships for orgs no longer in the list
      for (const orgId of originalOrgIds) {
        if (!newOrgIds.has(orgId)) {
          try {
            await deleteDoc(doc(db, 'organizations', orgId, 'members', selectedUser.uid));
            await deleteDoc(doc(db, 'users', selectedUser.uid, 'organizations', orgId));

            // Clean up pending invitations for this email in this org so
            // future re-invites aren't blocked.
            if (selectedUser.email) {
              const staleInvites = await getDocs(query(
                collection(db, 'invitations'),
                where('organizationId', '==', orgId),
                where('email', '==', selectedUser.email.toLowerCase()),
                where('status', '==', 'pending'),
              ));
              await Promise.all(staleInvites.docs.map((d) => deleteDoc(d.ref)));
            }
          } catch (err) {
            console.warn(`Failed to remove membership from org ${orgId}:`, err);
          }
        }
      }

      // Upsert memberships for all current assignments
      for (const assignment of editOrgAssignments) {
        if (!assignment.orgId) continue;
        const isNew = !originalOrgIds.has(assignment.orgId);
        if (isNew) {
          await setDoc(doc(db, 'organizations', assignment.orgId, 'members', selectedUser.uid), {
            userId: selectedUser.uid,
            email: selectedUser.email,
            displayName: editDisplayName,
            role: assignment.role,
            joinedAt: now,
            invitedBy: 'system',
          });
          await setDoc(doc(db, 'users', selectedUser.uid, 'organizations', assignment.orgId), {
            organizationId: assignment.orgId,
            role: assignment.role,
            joinedAt: now,
          });
        } else {
          const original = selectedUser.organizations.find(o => o.organizationId === assignment.orgId);
          if (original && original.role !== assignment.role) {
            await updateDoc(doc(db, 'organizations', assignment.orgId, 'members', selectedUser.uid), {
              role: assignment.role,
            });
            await updateDoc(doc(db, 'users', selectedUser.uid, 'organizations', assignment.orgId), {
              role: assignment.role,
            });
          }
        }
      }

      success(`User ${editDisplayName} updated successfully!`);

      // Reset and close
      setIsEditModalOpen(false);
      setSelectedUser(null);
      setEditDisplayName('');
      setEditSystemRole(null);
      setEditOrgAssignments([]);

      loadData();

    } catch (error: any) {
      console.error('Failed to update user:', error);
      setEditError(error.message || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    setDeleteLoading(true);

    try {
      const { getFirestore, doc, deleteDoc, collection, getDocs, query, where } = await import('firebase/firestore');
      const db = getFirestore();

      // Delete user profile from Firestore
      await deleteDoc(doc(db, 'users', selectedUser.uid));

      // Delete user's organization memberships
      const orgsSnapshot = await getDocs(collection(db, 'users', selectedUser.uid, 'organizations'));
      for (const orgDoc of orgsSnapshot.docs) {
        await deleteDoc(orgDoc.ref);

        // Also remove from organization's members
        const orgId = orgDoc.data().organizationId;
        try {
          await deleteDoc(doc(db, 'organizations', orgId, 'members', selectedUser.uid));
        } catch (err) {
          console.warn(`Failed to remove user from org ${orgId}:`, err);
        }
      }

      // Clean up any pending invitations for this email across all orgs —
      // otherwise future re-invites are blocked by stale pending records.
      if (selectedUser.email) {
        try {
          const staleInvites = await getDocs(query(
            collection(db, 'invitations'),
            where('email', '==', selectedUser.email.toLowerCase()),
            where('status', '==', 'pending'),
          ));
          await Promise.all(staleInvites.docs.map((d) => deleteDoc(d.ref)));
        } catch (err) {
          console.warn('Failed to clean up pending invitations:', err);
        }
      }

      success(`User ${selectedUser.displayName} deleted successfully!`);
      
      // Reset and close
      setIsDeleteModalOpen(false);
      setSelectedUser(null);
      
      // Reload users
      loadData();
      
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      showError(error.message || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Define columns for DataTable
  const columns: Column<User>[] = [
    {
      key: 'displayName',
      header: 'Name',
      sortable: true,
      render: (value, user) => (
        <span className="font-medium text-foreground">
          {user.displayName}
        </span>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      sortable: true,
      render: (value, user) => (
        <span className="text-muted-foreground">
          {user.email}
        </span>
      ),
    },
    {
      key: 'systemRole',
      header: 'System Role',
      render: (value, user) => {
        if (user.systemRole === 'super_admin') {
          return (
            <Badge variant="destructive" className="gap-1">
              <Shield className="w-3 h-3" />
              Super Admin
            </Badge>
          );
        }
        if (user.systemRole === 'support') {
          return (
            <Badge variant="outline" className="gap-1 border-status-warning text-status-warning bg-status-warning-bg">
              <Shield className="w-3 h-3" />
              Support
            </Badge>
          );
        }
        return <span className="text-sm text-muted-foreground/50">—</span>;
      },
    },
    {
      key: 'organizations',
      header: 'Organization Memberships',
      render: (value, user) => {
        if (user.organizations.length === 0) {
          return (
            <span className="text-sm text-muted-foreground/50 italic">
              No organizations
            </span>
          );
        }

        const roleStyles: Record<string, { bg: string; border: string; role: string }> = {
          owner:  { bg: 'bg-accent-orange/10', border: 'border-accent-orange/30', role: 'text-accent-orange' },
          admin:  { bg: 'bg-accent-blue/10',   border: 'border-accent-blue/30',   role: 'text-accent-blue' },
          member: { bg: 'bg-accent-green/10',  border: 'border-accent-green/30',  role: 'text-accent-green' },
          viewer: { bg: 'bg-muted',            border: 'border-border',           role: 'text-muted-foreground' },
        };

        const userOrgs = user.organizations.map(org => {
          const orgData = organizations.find(o => o.id === org.organizationId);
          return { name: orgData?.name || org.organizationId, role: org.role };
        });

        return (
          <div className="flex flex-col gap-1">
            {userOrgs.map((org, idx) => {
              const s = roleStyles[org.role] ?? roleStyles.viewer;
              return (
                <div
                  key={idx}
                  className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border text-xs ${s.bg} ${s.border}`}
                >
                  <Building className="w-3 h-3 text-muted-foreground/60 shrink-0" />
                  <span className="font-medium text-foreground">{org.name}</span>
                  <span className="text-muted-foreground/40">·</span>
                  <span className={`font-medium ${s.role}`}>
                    {org.role.charAt(0).toUpperCase() + org.role.slice(1)}
                  </span>
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      render: (value, user) => (
        <span className="text-sm text-muted-foreground">
          {fmt.formatDate(user.createdAt)}
        </span>
      ),
    },
    {
      key: 'lastSignInAt',
      header: 'Last Login',
      sortable: true,
      render: (value, user) => {
        if (user.lastSignInAt === undefined) {
          return <span className="text-sm text-muted-foreground/40">—</span>;
        }
        if (user.lastSignInAt === null) {
          return <span className="text-sm text-muted-foreground/60 italic">Never</span>;
        }
        return (
          <span
            className="text-sm text-muted-foreground"
            title={`Signed in ${fmt.formatDateTime(user.lastSignInAt)}${
              user.lastActiveAt ? ` · last active ${fmt.formatDateTime(user.lastActiveAt)}` : ''
            }`}
          >
            {formatRelativeTime(new Date(user.lastSignInAt))}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (value, user) => (
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(user);
            }}
          >
            <Edit className="w-3 h-3" />
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedUser(user);
              setIsDeleteModalOpen(true);
            }}
          >
            <Trash2 className="w-3 h-3 text-status-error" />
          </Button>
        </div>
      ),
    },
  ];

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <AppShell>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-display font-semibold text-foreground mb-2 flex items-center gap-2">
              <Users className="w-6 h-6" />
              User Management
            </h1>
            <p className="text-body text-muted-foreground">
              Create and manage users across all organizations
            </p>
          </div>
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="w-4 h-4" />
            Create User
          </Button>
        </div>

        {/* Users DataTable */}
        <Card padding="lg">
          {/* DataTableFilter - Linear-style column-based filtering */}
          <div className="mb-4 pb-4 border-b border-border">
            <DataTableFilter
              columns={filterColumns}
              filters={filters}
              actions={actions}
              strategy={strategy}
            />
          </div>

          <DataTable
            columns={columns}
            data={filteredUsers}
            keyExtractor={(row) => row.uid}
            searchable
            searchPlaceholder="Search users by name or email..."
            paginated
            defaultPageSize={10}
            emptyMessage="No users found"
            loading={loading}
          />
        </Card>

        {/* Create User Modal */}
        <Modal
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
            setCreateOrgAssignments([]);
            setCreateError('');
          }}
          title="Create New User"
          size="md"
        >
          <div className="space-y-4">
            <Input
              label="Email"
              type="email"
              value={createEmail}
              onChange={(e) => setCreateEmail(e.target.value)}
              placeholder="user@example.com"
              required
              disabled={creating}
            />

            <Input
              label="Password"
              type="password"
              value={createPassword}
              onChange={(e) => setCreatePassword(e.target.value)}
              placeholder="Min. 6 characters"
              required
              disabled={creating}
            />

            <Input
              label="Display Name"
              value={createDisplayName}
              onChange={(e) => setCreateDisplayName(e.target.value)}
              placeholder="John Doe"
              required
              disabled={creating}
            />

            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                System Role (Optional)
              </label>
              <select
                value={createSystemRole || ''}
                onChange={(e) => {
                  const value = e.target.value as any;
                  setCreateSystemRole(value || null);
                  if (value) {
                    setCreateOrgAssignments([]); // Clear orgs if system role selected
                  }
                }}
                disabled={creating}
                className="w-full px-3 py-2 rounded-md border border-border
                         bg-background
                         text-foreground
                         focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent
                         disabled:opacity-50 disabled:cursor-not-allowed
                         text-sm"
              >
                <option value="">None (Regular User)</option>
                <option value="super_admin">Super Admin</option>
                <option value="support">Support</option>
              </select>
              <p className="text-xs text-muted-foreground/70 mt-1">
                System roles have platform-wide access
              </p>
            </div>

            {!createSystemRole && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Organizations
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCreateOrgAssignments(prev => [...prev, { orgId: '', role: 'member' }])}
                    disabled={creating}
                  >
                    <Plus className="w-3 h-3" />
                    Add Organization
                  </Button>
                </div>
                {createOrgAssignments.length === 0 ? (
                  <p className="text-xs text-muted-foreground/60 italic py-1">
                    No organizations assigned. Click "Add Organization" to assign one.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {createOrgAssignments.map((assignment, idx) => {
                      const usedOrgIds = createOrgAssignments.map((a, i) => i !== idx ? a.orgId : '').filter(Boolean);
                      return (
                        <div key={idx} className="flex items-center gap-2">
                          <select
                            value={assignment.orgId}
                            onChange={(e) => setCreateOrgAssignments(prev =>
                              prev.map((a, i) => i === idx ? { ...a, orgId: e.target.value } : a)
                            )}
                            disabled={creating}
                            className="flex-1 px-3 py-2 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent disabled:opacity-50 text-sm"
                          >
                            <option value="">Select organization...</option>
                            {organizations.filter(o => !usedOrgIds.includes(o.id)).map(org => (
                              <option key={org.id} value={org.id}>{org.name}</option>
                            ))}
                          </select>
                          <select
                            value={assignment.role}
                            onChange={(e) => setCreateOrgAssignments(prev =>
                              prev.map((a, i) => i === idx ? { ...a, role: e.target.value as OrgRole } : a)
                            )}
                            disabled={creating}
                            className="px-3 py-2 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent disabled:opacity-50 text-sm"
                          >
                            <option value="owner">Owner</option>
                            <option value="admin">Admin</option>
                            <option value="member">Member</option>
                            <option value="viewer">Viewer</option>
                          </select>
                          <button
                            onClick={() => setCreateOrgAssignments(prev => prev.filter((_, i) => i !== idx))}
                            disabled={creating}
                            className="p-1.5 rounded text-muted-foreground hover:text-status-error hover:bg-status-error-bg disabled:opacity-50 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {createError && (
              <div className="p-3 rounded bg-status-error-bg border border-status-error">
                <p className="text-sm text-status-error">{createError}</p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => {
                  setIsCreateModalOpen(false);
                  setCreateOrgAssignments([]);
                  setCreateError('');
                }}
                disabled={creating}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleCreateUser}
                disabled={creating || !createEmail || !createPassword || !createDisplayName}
              >
                {creating ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Create User
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Edit User Modal */}
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedUser(null);
            setEditOrgAssignments([]);
            setEditError('');
          }}
          title="Edit User"
          size="md"
        >
          <div className="space-y-4">
            {editError && (
              <div className="p-3 rounded bg-status-error-bg border border-status-error">
                <p className="text-sm text-status-error">{editError}</p>
              </div>
            )}

            <div className="p-3 rounded bg-muted border border-border">
              <p className="text-sm text-muted-foreground">
                <strong>Email:</strong> {selectedUser?.email}
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Email cannot be changed
              </p>
            </div>

            <Input
              label="Display Name"
              value={editDisplayName}
              onChange={(e) => setEditDisplayName(e.target.value)}
              placeholder="John Doe"
              required
              disabled={editLoading}
            />

            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                System Role
              </label>
              <select
                value={editSystemRole || ''}
                onChange={(e) => setEditSystemRole(e.target.value === '' ? null : e.target.value as 'super_admin' | 'support')}
                disabled={editLoading}
                className="w-full px-3 py-2 rounded-md border border-border
                         bg-background
                         text-foreground
                         focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent
                         disabled:opacity-50 disabled:cursor-not-allowed
                         text-sm"
              >
                <option value="">None (Regular User)</option>
                <option value="super_admin">Super Admin</option>
                <option value="support">Support</option>
              </select>
              <p className="text-xs text-muted-foreground/70 mt-1">
                System roles have platform-wide access
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-muted-foreground">
                  Organizations
                </label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditOrgAssignments(prev => [...prev, { orgId: '', role: 'member' }])}
                  disabled={editLoading}
                >
                  <Plus className="w-3 h-3" />
                  Add Organization
                </Button>
              </div>
              {editOrgAssignments.length === 0 ? (
                <p className="text-xs text-muted-foreground/60 italic py-1">
                  No organizations assigned. Click "Add Organization" to assign one.
                </p>
              ) : (
                <div className="space-y-2">
                  {editOrgAssignments.map((assignment, idx) => {
                    const usedOrgIds = editOrgAssignments.map((a, i) => i !== idx ? a.orgId : '').filter(Boolean);
                    return (
                      <div key={idx} className="flex items-center gap-2">
                        <select
                          value={assignment.orgId}
                          onChange={(e) => setEditOrgAssignments(prev =>
                            prev.map((a, i) => i === idx ? { ...a, orgId: e.target.value } : a)
                          )}
                          disabled={editLoading}
                          className="flex-1 px-3 py-2 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent disabled:opacity-50 text-sm"
                        >
                          <option value="">Select organization...</option>
                          {organizations.filter(o => !usedOrgIds.includes(o.id)).map(org => (
                            <option key={org.id} value={org.id}>{org.name}</option>
                          ))}
                        </select>
                        <select
                          value={assignment.role}
                          onChange={(e) => setEditOrgAssignments(prev =>
                            prev.map((a, i) => i === idx ? { ...a, role: e.target.value as OrgRole } : a)
                          )}
                          disabled={editLoading}
                          className="px-3 py-2 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent disabled:opacity-50 text-sm"
                        >
                          <option value="owner">Owner</option>
                          <option value="admin">Admin</option>
                          <option value="member">Member</option>
                          <option value="viewer">Viewer</option>
                        </select>
                        <button
                          onClick={() => setEditOrgAssignments(prev => prev.filter((_, i) => i !== idx))}
                          disabled={editLoading}
                          className="p-1.5 rounded text-muted-foreground hover:text-status-error hover:bg-status-error-bg disabled:opacity-50 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex justify-between items-center gap-3 pt-4">
              <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={async () => {
                  if (!selectedUser) return;
                  try {
                    await adminResetMfa(selectedUser.uid);
                    success('Two-factor authentication reset for this user');
                  } catch (e: any) {
                    showError(e?.message || 'Failed to reset 2FA');
                  }
                }}
                disabled={editLoading}
              >
                Reset 2FA
              </Button>
              <Button
                variant="ghost"
                onClick={async () => {
                  if (!selectedUser) return;
                  setPwResetLoading(true);
                  setPwCopied(false);
                  try {
                    const r = await adminResetUserPassword(selectedUser.uid);
                    setPwResetResult({ email: r.email, password: r.password });
                  } catch (e: any) {
                    showError(e?.message || 'Failed to reset password');
                  } finally {
                    setPwResetLoading(false);
                  }
                }}
                disabled={editLoading || pwResetLoading}
              >
                {pwResetLoading ? <Spinner size="sm" className="mr-2" /> : null}
                Reset password
              </Button>
              </div>
              <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setSelectedUser(null);
                  setEditError('');
                }}
                disabled={editLoading}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleUpdateUser}
                disabled={editLoading || !editDisplayName}
              >
                {editLoading ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Edit className="w-4 h-4" />
                    Update User
                  </>
                )}
              </Button>
              </div>
            </div>
          </div>
        </Modal>

        {/* Delete User Modal */}
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSelectedUser(null);
          }}
          title="Delete User"
          size="sm"
        >
          <div className="space-y-4">
            {selectedUser && (
              <div className="p-4 bg-status-error-bg border border-status-error rounded">
                <p className="text-sm text-status-error">
                  Are you sure you want to delete <strong>{selectedUser.displayName}</strong>?
                </p>
                <p className="text-xs text-status-error mt-2">
                  Email: {selectedUser.email}
                </p>
                <p className="text-xs text-status-error mt-1">
                  This action cannot be undone.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setSelectedUser(null);
                }}
                disabled={deleteLoading}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleDeleteUser}
                disabled={deleteLoading}
                className="bg-status-error hover:bg-status-error/80"
              >
                {deleteLoading ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete User
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Show-once password result — the plaintext is unrecoverable once closed. */}
        <Modal
          isOpen={!!pwResetResult}
          onClose={() => setPwResetResult(null)}
          title="Password reset"
          size="sm"
        >
          {pwResetResult && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Share these with the user over a trusted channel. This password is shown only once —
                ask them to change it after signing in.
              </p>
              <div className="space-y-2">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Email</div>
                  <div className="font-mono text-sm text-foreground break-all">{pwResetResult.email ?? '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Temporary password</div>
                  <div className="font-mono text-sm text-foreground p-2 rounded border border-border bg-muted break-all">
                    {pwResetResult.password}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={async () => {
                    await navigator.clipboard.writeText(
                      `Email: ${pwResetResult.email ?? ''}\nPassword: ${pwResetResult.password}`,
                    );
                    setPwCopied(true);
                    success('Copied to clipboard');
                  }}
                >
                  {pwCopied ? 'Copied' : 'Copy'}
                </Button>
                <Button variant="default" onClick={() => setPwResetResult(null)}>Done</Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </AppShell>
  );
}

